import { yupResolver } from '@hookform/resolvers/yup';
import React, { useRef } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { array, object, string } from 'yup';
import { Alert } from '../../../../../../CommonComponents/OPL/Alert/Alert';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { Icon } from '../../../../../../CommonComponents/OPL/Icon/Icon';
import { IconButton } from '../../../../../../CommonComponents/OPL/IconButton/IconButton';
import { Input } from '../../../../../../CommonComponents/OPL/Input/Input';
import { Label } from '../../../../../../CommonComponents/OPL/Label/Label';
import { Modal, ModalProps } from '../../../../../../CommonComponents/OPL/Modal/Modal';
import { ModalBody } from '../../../../../../CommonComponents/OPL/Modal/ModalBody';
import { ModalCloseButton } from '../../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { ModalHeader } from '../../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { Select } from '../../../../../../CommonComponents/OPL/Select/Select/Select';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { Textarea } from '../../../../../../CommonComponents/OPL/Textarea/Textarea';
import { SingleDatePicker } from '../../../../../../CommonComponents/OPL/dates/SingleDatePicker/SingleDatePicker';
import { useInternalFileUploadMutation } from '../../../../../../DocumentVault/api/useInternalFileUploadMutation';
import { EstatePlanningDocumentDTO } from '../../../../../types/goalsDTO';
import { EstatePlanningDocumentType } from '../../EstatePlanning.types';
import { useCreateOrUpdateEstatePlanningDocumentMutation } from '../../api/useCreateOrUpdateEstatePlanningDocumentMutation';
import { Loading } from '../../../../../../CommonComponents/OPL/Loading/Loading';
import { useClientId } from '../../../../../../_hooks/useClientId';

interface AddEditDocumentModalProps extends Omit<ModalProps, 'onRequestClose'> {
  document: EstatePlanningDocumentDTO;
  documentTypes: { label: string; value: number }[];
  onRequestClose: () => void;
}

interface FormValues
  extends Omit<EstatePlanningDocumentDTO, 'DocumentType' | 'Guardians' | 'LastDocumentUpdate'> {
  DocumentType: { label: string; value: number };
  Guardians: { name: string }[];
  LastDocumentUpdate: string | null;
}

const validation = object().shape({
  Guardians: array().of(object().shape({ name: string().required('Required') })),
});

export const AddEditDocumentModal = ({
  documentTypes,
  document,
  isOpen,
  onRequestClose,
}: AddEditDocumentModalProps) => {
  const clientId = useClientId();

  const fileUploadInputRef = useRef<HTMLInputElement>(null);

  const {
    mutateAsync: createOrUpdateDocument,
    error: createOrUpdateDocumentError,
  } = useCreateOrUpdateEstatePlanningDocumentMutation();

  const {
    control,
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    watch,
    setValue,
  } = useForm<FormValues>({
    defaultValues: {
      ...document,
      DocumentType: documentTypes[document.DocumentType],
      Guardians: document.Guardians?.map(guardian => ({ name: guardian })) ?? [],
      LastDocumentUpdate: document.LastDocumentUpdate,
    },
    resolver: yupResolver(validation),
  });

  const documentType = watch('DocumentType');
  const filePath = watch('FileUrl');

  const { fields, append, remove } = useFieldArray<FormValues>({
    control,
    name: 'Guardians',
  });

  const { mutateAsync: uploadDocument, isLoading: isUploading } = useInternalFileUploadMutation();

  const onFileSelected = async file => {
    const path = `Estate Planning/${documentType.label}/`;

    await uploadDocument({ clientId: clientId.toString(), path, file });

    setValue('FileUrl', `${path}${file.name}`);
  };

  const onSubmit = async (values: FormValues) => {
    const updatedDocument = {
      ...values,
      DocumentType: values.DocumentType.value,
      Guardians: values.Guardians.map(guardian => guardian.name),
      LastDocumentUpdate: values.LastDocumentUpdate ?? null,
    };

    await createOrUpdateDocument({ ...updatedDocument, clientId: clientId.toString() });

    onRequestClose();
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} aria={{ labelledby: 'modal-header' }}>
      <ModalHeader as="h2" id="modal-header">
        Add Document
      </ModalHeader>
      <ModalCloseButton onClick={onRequestClose} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          {createOrUpdateDocumentError && (
            <Alert variant="error" message={createOrUpdateDocumentError.message} mb="24" />
          )}

          <Controller
            control={control}
            name="DocumentType"
            render={({ field: { value, onChange } }) => (
              <Select
                value={value}
                options={documentTypes}
                onChange={onChange}
                aria-label="Select Document Type"
              />
            )}
          />

          <Input
            {...register('Description')}
            type="text"
            label="Description"
            formVariant={errors.Description ? 'error' : undefined}
            helperText={errors.Description?.message}
            mt="16"
            aria-label="Enter Description"
          />

          <Text as="p" variant="formLabel" css="display: block;" mb="4" mt="16">
            Last Updated Date
          </Text>

          <Controller
            control={control}
            name="LastDocumentUpdate"
            render={({ field: { value, onChange } }) => {
              const dateValue = value ? new Date(value) : undefined;

              return (
                <SingleDatePicker
                  date={dateValue ?? undefined}
                  onChange={onChange}
                  maxDate={new Date()}
                  minDate={new Date(1900, 0)}
                />
              );
            }}
          />

          <Label mt="16" css="display: block;" htmlFor="Select Document">
            Document (Optional)
          </Label>

          <>
            <input
              ref={fileUploadInputRef}
              onChange={ev => ev.target.files && onFileSelected(ev.target.files[0])}
              type="file"
              style={{ display: 'none' }}
              aria-label="Select Document"
              id="Select Document"
            />

            <Button
              mt="4"
              variant="secondary"
              css="width: 100%;"
              leftIcon="far fa-arrow-circle-up"
              onClick={() => fileUploadInputRef.current?.click()}
            >
              Upload File
            </Button>
          </>

          {isUploading && (
            <Box mt="8">
              <Loading size="small" />
            </Box>
          )}

          {!isUploading && filePath !== null && (
            <Box display="flex" alignItems="center" mt="4">
              <Icon className="far fa-file-alt" mr="8" />

              <Text css="flex: 1;">{filePath.substring(filePath.lastIndexOf('/') + 1)}</Text>

              <IconButton
                variant="subtleInactive"
                icon="far fa-minus-circle"
                onClick={() => setValue('FileUrl', null)}
                ml="8"
              />
            </Box>
          )}

          {documentType.value === EstatePlanningDocumentType.Will && (
            <>
              <Text as="p" variant="formLabel" css="display: block;" mt="16">
                Guardian Designation for Children
              </Text>

              {fields.map((field, index) => (
                <React.Fragment key={field.id}>
                  <Box display="flex" alignItems="start" mt="4">
                    <Input
                      key={field.id}
                      {...register(`Guardians.${index}.name`)}
                      type="text"
                      label="First and Last Name"
                      formVariant={errors.Guardians?.[index]?.name ? 'error' : undefined}
                      helperText={errors.Guardians?.[index]?.name?.message}
                      css="flex: 1;"
                    />

                    <IconButton
                      variant="subtleInactive"
                      icon="far fa-minus-circle"
                      onClick={() => remove(index)}
                      ml="8"
                      mt="24"
                      aria-label="Remove Guardian"
                    />
                  </Box>
                </React.Fragment>
              ))}

              <Button
                leftIcon="fas fa-plus"
                variant="subtleActive"
                onClick={() => append({ name: '' })}
                mt="4"
              >
                Add Guardian
              </Button>
            </>
          )}

          <Textarea
            {...register('Notes')}
            label="Notes"
            formVariant={errors.Notes ? 'error' : undefined}
            helperText={errors.Notes?.message}
            mt="16"
          />
        </ModalBody>

        <ModalFooter>
          <Button variant="subtleInactive" onClick={onRequestClose}>
            Cancel
          </Button>

          <Button type="submit" isLoading={isSubmitting} ml="8">
            Save
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};
